/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	getHygraph,
	getOwnerConfigFromEnv,
	getPreviewRedirectPath,
	toHygraphSlug,
	resolveHygraphTarget,
} from "@cosmos/hygraph-data";
import { Locale } from "@cosmos/hygraph-schema";
import { getSdk } from "@cosmos/hygraph-schema/dist/generated/resolvers.hygraph";
import { getSlugParam, getHygraphLocale, PageSlugProps, SearchParams } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { redirect } from "next/navigation";

export type CosmosTemplateKey =
	| "cosmosPageHeroAndSection"
	| "cosmosPageDirectory"
	| "cosmosPageBlogDetail"
	| "cosmosPageEulaKey"
	| "cosmosPageRedirect"
	| "cosmosPageLegalContent"
	| "cosmosPageSearch"
	| "cosmosPageResearch";

/**
 * Parent path of a multi-segment slug, or `null` when there is no parent
 * (single-segment or root). `/a/b/c` → `/a/b`; `/a` → `null`. Used by the
 * directory-detail fallback to test whether the path one level up is a
 * `CosmosPageDirectory`.
 */
const getParentSlug = (slug: string): string | null => {
	const idx = slug.lastIndexOf("/");
	return idx > 0 ? slug.slice(0, idx) : null;
};

/**
 * Serializes searchParams back to a query string ("" when empty) for redirect targets.
 * `undefined` values are intentionally omitted: Next.js types searchParams values as
 * possibly-undefined purely for absent-key index access, so an undefined entry means
 * "param not present" and must not appear in the redirect URL.
 */
const serializeSearchParams = (searchParams: SearchParams): string => {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(searchParams)) {
		if (typeof value === "string") {
			params.append(key, value);
		} else if (Array.isArray(value)) {
			for (const item of value) {
				params.append(key, item);
			}
		}
	}
	const queryString = params.toString();
	return queryString ? `?${queryString}` : "";
};

/**
 * Resolves which Cosmos page template (if any) serves the requested slug.
 *
 * Side effect: when the request carries a valid `?preview=` token and the path still
 * includes the owner's pageSlugPrefix (Hygraph live-preview links), this throws Next.js's
 * NEXT_REDIRECT to the prefix-stripped URL instead of returning — safe from page render
 * and generateMetadata, but do not call it outside a Next.js request context.
 */
export const getKey = async (props: PageSlugProps, hygraphLocales: string[]): Promise<CosmosTemplateKey | null> => {
	const urlSlug = getSlugParam(props.params);

	const previewParam = props.searchParams.preview;
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	const target = await resolveHygraphTarget();
	const resolvedEndpoint = target?.endpoint;
	const resolvedOwnerId = target?.ownerId;

	const ownerConfig = await getOwnerConfigFromEnv(preview, resolvedOwnerId, resolvedEndpoint);

	// Hygraph live-preview links interpolate the raw owner-prefixed page slug (e.g. "/lore/home");
	// send the editor to the real prefix-stripped URL, keeping the preview param intact. Locale
	// handling is left to middleware, exactly as for the original locale-less preview URL.
	const previewRedirectPath = getPreviewRedirectPath(urlSlug, preview, ownerConfig.pageSlugPrefix);
	if (previewRedirectPath) {
		redirect(`${previewRedirectPath}${serializeSearchParams(props.searchParams)}`);
	}

	const hygraphSlug = toHygraphSlug(urlSlug, ownerConfig.pageSlugPrefix);

	const sdk = getSdk(getHygraph(preview, resolvedEndpoint));
	const localeArray = [getHygraphLocale(hygraphLocales, props.params.lang), Locale.En] as Locale[];

	// Degrade a failed template lookup to "no template" (→ notFound() → 404) rather than
	// letting it bubble to a 500. This matters most in preview: a misconfigured, missing, or
	// expired draft-stage client (e.g. a shared-project tenant whose SITE_<X>_HYGRAPH_PREVIEW_*
	// env hasn't deployed yet) makes this query throw, and getKey runs unguarded in both
	// templateSelector and metadataSelector. The render and metadata fetch paths already
	// notFound() on error; this closes the one gap that turned a clean 404 into a server error.
	let res: Awaited<ReturnType<typeof sdk.CosmosPageTemplates>>;
	try {
		res = await sdk.CosmosPageTemplates({
			slug: hygraphSlug,
			locale: localeArray,
		});
	} catch (e) {
		console.error("[Template Selector] getKey() failed to fetch CosmosPageTemplates", {
			error: isError(e) ? e.message : String(e),
			slug: hygraphSlug,
			endpoint: resolvedEndpoint,
			ownerId: resolvedOwnerId,
			preview: preview ? "enabled" : "disabled",
		});
		return null;
	}

	const templateKeys: CosmosTemplateKey[] = Object.keys(res) as CosmosTemplateKey[];

	for (let i = 0; i < templateKeys.length; i++) {
		const key = templateKeys[i];
		const value = res[key as keyof typeof res] as { __typename?: string; id?: string } | undefined | null;
		if (value?.__typename && value?.id) {
			return key;
		}
	}

	// Directory-detail fallback: the exact path matched no template, so check
	// whether its PARENT is a CosmosPageDirectory (e.g. a service-partner detail
	// at `<directory>/<partner-slug>`). A detail page is NOT its own content
	// type — it is the same `cosmosPageDirectory` template rendered in detail
	// mode, so route it to that key and let the page branch on the extra path
	// segment. The extra query only runs on paths that would otherwise 404, so
	// normal page loads pay nothing for it.
	const parentUrlSlug = getParentSlug(urlSlug);
	if (parentUrlSlug) {
		const parentHygraphSlug = toHygraphSlug(parentUrlSlug, ownerConfig.pageSlugPrefix);
		try {
			const parentRes = await sdk.CosmosPageTemplates({ slug: parentHygraphSlug, locale: localeArray });
			const directory = parentRes.cosmosPageDirectory;
			if (directory?.__typename && directory?.id) {
				return "cosmosPageDirectory";
			}
		} catch (e) {
			console.error("[Template Selector] getKey() failed to fetch parent CosmosPageTemplates", {
				error: isError(e) ? e.message : String(e),
				slug: parentHygraphSlug,
				endpoint: resolvedEndpoint,
				ownerId: resolvedOwnerId,
				preview: preview ? "enabled" : "disabled",
			});
			return null;
		}
	}

	return null;
};

const slugRegEx = /^\/[a-z0-9]+(?:[a-z0-9-+/]+)*$/;
const phpPathRegex = /\/[^/]*\.php\//;

const blockedPaths = new Set(["/static/", "/wp-content", "/_profiler", "/docs/awesomeads", "/debug/", "/dev/"]);
const blockedSubstrings = new Set([".", "%", "sssied", "fsssied"]);

/**
 * Validates a slug string for expected normal traits to try and block bots
 * @param str
 * @returns Boolean
 */
const checkSlugString = (str?: string): boolean => {
	if (!str) return true;
	if (str.length > 200) return false;

	for (const path of blockedPaths) {
		if (str.startsWith(path)) return false;
	}

	if (phpPathRegex.test(str)) return false;

	for (const substring of blockedSubstrings) {
		if (str.includes(substring)) return false;
	}

	return slugRegEx.test(str);
};

const localeRegEx = /[a-zA-Z]{2}(?:-[a-zA-Z]{4}|-[a-zA-Z]{2})?/;

/**
 * Validates a lang string for expected normal traits to try and block bots
 * @param str
 * @returns Boolean
 */
const checkLangString = (str?: string): boolean => {
	if (!str) return false;
	if (str.length > 200) return false;

	const hasInvalidChars = str.indexOf(".") !== -1 || str.indexOf("cosmos") !== -1;

	if (hasInvalidChars) {
		return false;
	}

	if (!localeRegEx.test(str)) {
		return false;
	}

	return true;
};

export const checkValidParams = (props: PageSlugProps): boolean => {
	const slug = getSlugParam(props.params);

	if (!checkSlugString(slug) || !checkLangString(props.params.lang)) {
		console.debug("checkValidParams() found invalid slug or invalid lang param");
		return false;
	}

	return true;
};
